class CompareBar extends HTMLElement {
    constructor() {
        super();
        this.allElements = {
            compare_product: '[data-compare-product]',
            bar_template: 'cardBarTemplate',
            compare_bar_cards: '.compare-bar__cards',
            remove_all: '[data-remove-all]',
            compare_products: '[data-compare-products]',
            remove_card: 'data-card-remove'
        };

        this.initialize();
    }

    initialize() {
        // First, check if there are checkboxes with the data-compare-product attribute
        const checkboxes = document.querySelectorAll(this.allElements.compare_product);
        if (checkboxes.length === 0) {
            console.warn('No checkboxes found with data-compare-product attribute.');
            return; // Exit the constructor early if no such checkboxes are found
        }

        // Load allHandles from localStorage, remove duplicates, and update localStorage
        let storedHandles = JSON.parse(localStorage.getItem('allHandles')) || [];
        this.allHandles = [...new Set(storedHandles)];
        localStorage.setItem('allHandles', JSON.stringify(this.allHandles));

        // Fetch product data and prepare for injection
        this.prepareProductData().then(() => {
            console.log('Product data prepared.');
        });

        // Card bar template
        this.cardBarTemplate = document
            .getElementById(this.allElements.bar_template)
            .textContent.trim();

        // Cards on bar
        this.cardsOnBar = this.querySelector(this.allElements.compare_bar_cards);

        // Init the checkboxes
        this.initCheckboxesAndListeners();

        // Check if allHandles has values and set checkboxes accordingly
        this.checkIfHandlesExistAndSetCheckboxes();

        // Find the clear button and attach an event listener
        this.clearButton = this.querySelector(this.allElements.remove_all);
        if (this.clearButton) {
            this.clearButton.addEventListener('click', this.clearAll.bind(this));
        }

        // Find the redirect button and attach an event listener
        this.redirectButton = this.querySelector(this.allElements.compare_products);
        if (this.redirectButton) {
            this.redirectButton.addEventListener(
                'click',
                this.redirectToCompareProducts.bind(this)
            );
            if (this.allHandles.length > 1) this.redirectButton.removeAttribute('disabled');
        }

        // disable the compare checkbox if limit is reached
        this.disableUncheckedCheckboxes();
    }

    async prepareProductData() {
        const productDataArray = await this.fetchProductData(this.allHandles);
        if (!productDataArray) return;
        this.handleProductData(productDataArray);
    }

    handleProductData(productDataArray) {
        productDataArray.forEach((data) => {
            this.injectCardTemplate(data.handle, data.thumbnail, data.title);
        });
    }

    async fetchProductData(handles) {
        const urls = handles.map((handle) => `/products/${handle}?view=compare`);
        const promises = urls.map((url) => fetch(url).then((response) => response.json()));

        try {
            const responses = await Promise.all(promises);
            return responses.map((response) => ({
                handle: response.handle,
                thumbnail: response.featured_image,
                title: response.title
            }));
        } catch (error) {
            console.error('Failed to fetch product data:', error);
            return [];
        }
    }

    checkIfHandlesExistAndSetCheckboxes() {
        // Check if allHandles has values
        if (this.allHandles.length > 0) {
            // Loop through checkboxes
            this.checkboxes.forEach((checkbox) => {
                // Check if the checkbox data-handle is equal to the handle in this.allHandles
                const handleExists = this.allHandles.some(
                    (handle) => handle === checkbox.dataset.handle
                );
                if (handleExists) {
                    checkbox.checked = true; // Set the checkbox to checked if the handle exists
                }
            });
        }
    }

    initCheckboxesAndListeners() {
        // Find checkboxes with a data-handle attribute and check them
        this.checkboxes = Array.from(document.querySelectorAll(this.allElements.compare_product));

        // Add event listeners to checkboxes
        this.checkboxes.forEach((checkbox) => {
            checkbox.addEventListener('click', this.onCheckHandler.bind(this));
        });

        // Attach event delegation for dynamically added buttons
        this.cardsOnBar.addEventListener('click', (event) => {
            if (event.target.hasAttribute(this.allElements.remove_card)) {
                const button = event.target;
                const handleToRemove = button.getAttribute('data-handle');

                // Remove the handle from allHandles
                const index = this.allHandles.indexOf(handleToRemove);
                if (index > -1) {
                    this.allHandles.splice(index, 1);
                }

                // Uncheck the checkbox with the same handle
                const checkbox = this.checkboxes.find(
                    (checkbox) => checkbox.dataset.handle === handleToRemove
                );
                if (checkbox) {
                    checkbox.checked = false;
                }

                // Clear the card associated with the handle
                this.clearCard(handleToRemove);

                // Remove the parent container of the button
                button.parentNode.remove();

                // Call the function to disable unchecked checkboxes
                this.disableUncheckedCheckboxes();
            }
        });
    }

    // Define the function to disable unchecked checkboxes
    disableUncheckedCheckboxes() {
        this.checkboxes.forEach((checkbox) => {
            // Check if the checkbox is not checked and the length of allHandles is 3
            if (!checkbox.checked && this.allHandles.length >= 3) {
                checkbox.disabled = true;
            } else {
                checkbox.disabled = false;
            }

            if (this.allHandles.length >= 2) {
                this.redirectButton.removeAttribute('disabled');
            } else {
                this.redirectButton.setAttribute('disabled', true);
            }
        });
    }

    // Call this function in the onCheckHandler method after updating allHandles
    onCheckHandler(event) {
        const checkbox = event.target;
        const handle = checkbox.dataset.handle;
        const thumbnail = checkbox.dataset.thumbnail;
        const title = checkbox.dataset.title;

        // Determine if the checkbox was checked or unchecked
        const isChecked = checkbox.checked;

        if (isChecked) {
            // Checkbox was checked
            // Add the handle to the allHandles array
            this.allHandles.push(handle);
            // Save allHandles to localStorage
            localStorage.setItem('allHandles', JSON.stringify(this.allHandles));

            // Inject the modified template into the next available card on the bar
            this.injectCardTemplate(handle, thumbnail, title);
        } else {
            // Checkbox was unchecked
            // Remove the handle from the allHandles array
            const index = this.allHandles.indexOf(handle);
            if (index > -1) {
                this.allHandles.splice(index, 1);
            }
            // Save allHandles to localStorage
            localStorage.setItem('allHandles', JSON.stringify(this.allHandles));

            // Clear the card associated with the unchecked checkbox and add the "empty" class
            this.clearCard(handle);
        }

        // Call the function to disable unchecked checkboxes
        this.disableUncheckedCheckboxes();
    }

    // Function to inject the modified template into the next available card on the bar
    injectCardTemplate(handle, thumbnail, title) {
        // if card with handle exist, don't re-inject it
        if (this.cardsOnBar.querySelector(`[data-handle="${handle}"]`)) return;

        // Replace placeholders in the template with actual values
        const templateWithValues = this.cardBarTemplate.replace(/#\w+#/g, (match) => {
            // Check if the placeholder is #handle#, #image#, or #title#
            switch (match) {
                case '#handle#':
                    return handle;
                case '#image#':
                    return thumbnail;
                case '#title#':
                    return title;
                default:
                    // Return the original match if none of the above conditions are met
                    return match;
            }
        });

        const card = document.createElement('div');
        card.classList.add('compare-bar__card');
        card.setAttribute('data-handle', handle);
        card.innerHTML = templateWithValues;

        this.cardsOnBar.appendChild(card);
    }

    // Function to clear the card associated with the unchecked checkbox and add the "empty" class
    clearCard(handle) {
        // Directly select and remove the card from the DOM
        const cardToRemove = this.cardsOnBar.querySelector(`[data-handle="${handle}"]`);

        if (cardToRemove) {
            cardToRemove.remove();
        }

        // Find the index of the handle in allHandles and remove it
        const index = this.allHandles.indexOf(handle);
        if (index !== -1) {
            this.allHandles.splice(index, 1); // Remove the handle from the array
        }

        // Update allHandles in localStorage
        localStorage.setItem('allHandles', JSON.stringify(this.allHandles));

        // Call the function to disable unchecked checkboxes
        this.disableUncheckedCheckboxes();
    }

    clearAll() {
        // Uncheck all checkboxes
        this.checkboxes.forEach((checkbox) => {
            checkbox.checked = false;
        });

        // Empty all cards on the bar and add the "empty" class
        this.cardsOnBar.querySelectorAll(`[data-handle]`).forEach((card) => {
            card.remove();
        });

        // clear all handles
        this.allHandles = [];

        // Update allHandles in localStorage
        localStorage.setItem('allHandles', JSON.stringify(this.allHandles));

        // Call the function to disable unchecked checkboxes
        this.disableUncheckedCheckboxes();
    }

    redirectToCompareProducts() {
        // Construct the URL with prods query parameter
        let url = '/pages/compare-products?prods=';
        // Join allHandles with commas
        const handles = this.allHandles.join(',');
        // Append the handles to the URL
        url += handles;

        // Redirect to the constructed URL
        window.location.href = url;
    }
}

customElements.define('compare-bar', CompareBar);
